import ThemedDialog from 'Components/_NewElements/Dialog';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import { Container, Item } from 'Components/Grid';
import { partialDateFromDate } from 'neuro-utils';
import React from 'react';
import { useIntl } from 'react-intl';
import { getFile } from 'Store/documents/helpers/apiFetchers';
import { formatPartialDate } from 'Utility/date';
import colors from '../../config/theme/colors';

import type { IActionButtonRounded } from 'Components/ActionButtonRounded';

const ButtonProps: Partial<IActionButtonRounded> = {
  width: 12,
  height: 3,
  fontSize: 12,
};

const FileInfo = ({ file }: IFileInfo) => {
  const date = file?.date ? formatPartialDate(partialDateFromDate(new Date(file.date))) : '';
  const name = file?.name;
  return (
    <div style={{ textAlign: 'center', lineHeight: '1.4rem' }}>
      <span style={{ fontWeight: 600 }}>{date}</span>
      {` - ${name}`}
    </div>
  );
};

interface IFileInfo {
  file: IDocumentWithTypeV2<'filestorefile'> | undefined;
}

const RenderFile = ({ file }: IRenderFile) => {
  const [attachment, setAttachment] = React.useState<Blob | null>(null);

  React.useEffect(() => {
    if (file) {
      void getFile(file._id, file.fileContentId).then(res => {
        if (res) setAttachment(res);
      });
    }
  }, [file]);

  return file && attachment ? (
    <>
      {file.contentType === 'application/pdf' ? (
        <iframe style={{ width: '100%', minHeight: '25rem', height: '82vh' }} src={URL.createObjectURL(attachment)} />
      ) : (
        <img style={{ width: '100%' }} src={URL.createObjectURL(attachment)} />
      )}
    </>
  ) : (
    <></>
  );
};

interface IRenderFile {
  file: IDocumentWithTypeV2<'filestorefile'> | undefined;
}

const ChooseFromFileList = ({ files, setCompare }: IChooseFromFileList) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  return (
    <div style={{ display: 'grid', rowGap: '2rem', padding: '2rem' }}>
      <h3 style={{ color: colors.primary }}>{fm('patientFiles.chooseFile')}</h3>
      {files.map(f => (
        <button
          key={f._id}
          style={{
            border: '0.1rem solid ' + colors.primary,
            borderRadius: '0.5rem',
            cursor: 'pointer',
            padding: '1rem 1rem',
            textAlign: 'left',
            backgroundColor: 'white',
          }}
          onClick={() => setCompare(f._id)}
        >
          <FileInfo file={f} />
        </button>
      ))}
    </div>
  );
};

interface IChooseFromFileList {
  files: Array<IDocumentWithTypeV2<'filestorefile'>>;
  setCompare: (id: string) => void;
}

const FileCompareDialog = ({ files, firstCompare, secondCompare, closeDialog }: IFileCompareDialog) => {
  const [firstCompareFile, setFirstCompareFile] = React.useState<IDocumentWithTypeV2<'filestorefile'> | undefined>(
    firstCompare,
  );
  const [secondCompareFile, setSecondCompareFile] = React.useState<IDocumentWithTypeV2<'filestorefile'> | undefined>(
    secondCompare,
  );
  const [choosingSide, setChoosingSide] = React.useState<'left' | 'right' | null>(null);

  React.useEffect(() => {
    if (firstCompare && firstCompare !== firstCompareFile) setFirstCompareFile(firstCompare);
    if (!firstCompare) setSecondCompareFile(undefined);
  }, [firstCompare]);

  const filesOtherThanFirst = files.filter(f => f._id !== firstCompare?._id);

  return (
    <ThemedDialog
      dialogProps={{ fullScreen: true }}
      open={!!firstCompare}
      dialogActions={[{ text: 'general.close', onClick: () => closeDialog() }]}
      dialogContentProps={{ style: { paddingBottom: 0 } }}
    >
      <Container>
        {firstCompareFile ? (
          <Item xs>
            <RenderFile file={firstCompareFile} />
            <FileInfo file={firstCompareFile} />
            {/* <ActionButtonRounded
              text="general.chooseAnotherFile"
              onClick={() => setFirstCompareFile(undefined)}
              {...ButtonProps}
            /> */}
          </Item>
        ) : choosingSide === 'left' ? (
          <Item xs>
            <ChooseFromFileList
              files={files}
              setCompare={(id: string) => {
                setFirstCompareFile(files.find(f => f._id === id));
                setChoosingSide(null);
              }}
            />
          </Item>
        ) : (
          <Item xs="auto">
            <div style={{ padding: '2rem' }}>
              <ActionButtonRounded
                text="general.compareToAnother"
                onClick={() => setChoosingSide('left')}
                {...ButtonProps}
              />
            </div>
          </Item>
        )}
        {secondCompareFile ? (
          <Item xs>
            <RenderFile file={secondCompareFile} />
            <FileInfo file={secondCompareFile} />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
              <ActionButtonRounded
                text="general.chooseAnotherFile"
                onClick={() => {
                  setSecondCompareFile(undefined);
                  setChoosingSide('right');
                }}
                {...ButtonProps}
              />
              <div style={{ marginLeft: '2rem' }}>
                <ActionButtonRounded
                  text="patientFiles.closeFile"
                  onClick={() => {
                    setSecondCompareFile(undefined);
                    setChoosingSide(null);
                  }}
                  {...ButtonProps}
                />
              </div>
            </div>
          </Item>
        ) : choosingSide === 'right' ? (
          <Item xs>
            <ChooseFromFileList
              files={filesOtherThanFirst}
              setCompare={(id: string) => {
                setSecondCompareFile(files.find(f => f._id === id));
                setChoosingSide(null);
              }}
            />
          </Item>
        ) : (
          <Item xs="auto">
            {filesOtherThanFirst.length > 0 ? (
              <div style={{ padding: '2rem' }}>
                <ActionButtonRounded
                  text="general.compareToAnother"
                  onClick={() => setChoosingSide('right')}
                  {...ButtonProps}
                />
              </div>
            ) : (
              <></>
            )}
          </Item>
        )}
      </Container>
    </ThemedDialog>
  );
};

interface IFileCompareDialog {
  files: Array<IDocumentWithTypeV2<'filestorefile'>>;
  firstCompare?: IDocumentWithTypeV2<'filestorefile'>; // id
  secondCompare?: IDocumentWithTypeV2<'filestorefile'>;
  closeDialog: () => void;
}

export default FileCompareDialog;
